import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CompTag } from '@/components/CompTag';
import { Countdown } from '@/components/Countdown';
import { LoomliteMark } from '@/components/LoomliteMark';
import { OAuthButton } from '@/components/OAuthButton';
import { useAuth } from '@/contexts/AuthContext';
import { useCompetitions } from '@/hooks/useCompetitions';
import { formatDate } from '@/lib/utils';

export function LoginPage() {
	const { user, signInWithEmail, signUpWithEmail, signInWithOAuth } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { data: competitions } = useCompetitions();
	const featured = competitions?.find((c) => c.Status === 'open') ?? competitions?.[0];

	const [mode, setMode] = useState<'signin' | 'signup'>('signin');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	if (user) {
		const redirect = (location.state as { from?: string } | null)?.from ?? '/';
		return <Navigate to={redirect} replace />;
	}

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		setBusy(true);
		setError(null);
		const op = mode === 'signin' ? signInWithEmail : signUpWithEmail;
		const { error: err } = await op(email, password);
		setBusy(false);
		if (err) setError(err);
		else navigate('/');
	};

	const oauth = async (provider: 'google' | 'discord') => {
		const { error: err } = await signInWithOAuth(provider);
		if (err) setError(err);
	};

	const totalTypes = featured?.AllowedEntityTypes?.length ?? 0;

	const headingParts = (() => {
		const name = featured?.Name ?? 'The Autumn Compendium';
		const words = name.split(' ');
		if (words.length === 1) return { lead: 'Welcome to', tail: name };
		// Destructured with defaults rather than indexed: a `.length` check does
		// not narrow an index access, so `words[0]` is `string | undefined`.
		const [firstWord = ''] = words;
		const startsWithThe = firstWord.toLowerCase() === 'the';
		const lead = startsWithThe ? words.slice(0, 2).join(' ') : `The ${firstWord}`;
		const tail = startsWithThe ? words.slice(2).join(' ') : words.slice(1).join(' ');
		return { lead, tail: tail || words.at(-1) || name };
	})();

	return (
		<div className="ll-bg-app min-h-screen">
			<div className="border-b border-loomlite-border bg-loomlite-ink/50 backdrop-blur">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-8 py-3.5">
					<LoomliteMark size={26} />
					{featured && (
						<div className="flex items-center gap-3">
							<CompTag color="gold">{featured.Name} · Featured</CompTag>
							{featured.Status === 'open' && (
								<Countdown compact targetIso={featured.SubmissionCloseAt} />
							)}
						</div>
					)}
				</div>
			</div>

			<div className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-8 px-8 py-12 lg:grid-cols-[1.1fr_1fr]">
				<div className="flex flex-col justify-center gap-6">
					<div className="flex items-center gap-2">
						<CompTag color="purple">Community Competition · S3</CompTag>
					</div>
					<h1 className="m-0 font-heading text-5xl font-bold leading-[1.05] tracking-tight sm:text-[52px]">
						{headingParts.lead}
						<br />
						<span className="ll-gradient-text">{headingParts.tail}</span>
					</h1>
					<p className="m-0 max-w-[440px] text-[17px] text-loomlite-muted">
						{featured?.Description ??
							'Loomlite is the all-in-one TTRPG campaign manager for Game Masters. Submit a character, campaign, location or written entry from your library: judges score every piece on originality, craft, and table usefulness.'}
					</p>
					{featured && (
						<div className="mt-2 flex gap-6 text-sm text-loomlite-muted">
							<div>
								<span className="block font-heading text-xl font-semibold text-loomlite-text">
									{featured.MaxEntriesPerUser}
								</span>
								{featured.MaxEntriesPerUser === 1 ? 'entry per table' : 'entries per table'}
							</div>
							<div>
								<span className="block font-heading text-xl font-semibold text-loomlite-text">
									{totalTypes}
								</span>
								content types
							</div>
							<div>
								<span className="block font-heading text-xl font-semibold text-loomlite-text">
									{formatDate(featured.SubmissionCloseAt).split(',')[0]}
								</span>
								submissions close
							</div>
						</div>
					)}
				</div>

				<div className="flex items-center justify-center">
					<div className="w-full max-w-md rounded-xl border border-loomlite-border bg-loomlite-card p-7">
						<h2 className="m-0 font-heading text-[22px] font-semibold text-loomlite-text">
							{mode === 'signin' ? 'Sign in to submit' : 'Create your account'}
						</h2>
						<p className="m-0 mb-5 mt-1 text-sm text-loomlite-muted">
							Your Loomlite account is your entry pass.
						</p>

						<div className="flex flex-col gap-2.5">
							<OAuthButton provider="google" onClick={() => oauth('google')} disabled={busy} />
							<OAuthButton provider="discord" onClick={() => oauth('discord')} disabled={busy} />
						</div>

						<div className="ll-divider my-4">
							<span>or continue with email</span>
						</div>

						<form onSubmit={submit} className="flex flex-col gap-3.5">
							<div>
								<Label htmlFor="email">Email address</Label>
								<Input
									id="email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="you@table.com"
									required
								/>
							</div>
							<div>
								<div className="flex items-baseline justify-between">
									<Label htmlFor="password">Password</Label>
									{mode === 'signin' && (
										<span className="text-xs text-loomlite-purple-soft">Forgot?</span>
									)}
								</div>
								<Input
									id="password"
									type="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
								/>
							</div>
							{error && <p className="text-sm text-red-400">{error}</p>}
							<Button type="submit" className="mt-1 w-full" disabled={busy}>
								{mode === 'signin' ? 'Sign in & continue' : 'Create account & continue'}
							</Button>
							<p className="m-0 mt-1.5 text-center text-xs text-loomlite-muted">
								{mode === 'signin' ? (
									<>
										New to Loomlite?{' '}
										<button
											type="button"
											onClick={() => setMode('signup')}
											className="text-loomlite-purple-soft hover:underline"
										>
											Create an account
										</button>
									</>
								) : (
									<>
										Already have an account?{' '}
										<button
											type="button"
											onClick={() => setMode('signin')}
											className="text-loomlite-purple-soft hover:underline"
										>
											Sign in
										</button>
									</>
								)}
							</p>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
}
